import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Paper from '@material-ui/core/Paper';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import TableFilter from 'react-table-filter';
import 'react-table-filter/lib/styles.css';


import {
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  TableChart,
  BorderColor,
} from "@material-ui/icons";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LocalShipping } from '@material-ui/icons';
import CancelIcon from '@mui/icons-material/Cancel';
import { InfoRounded } from '@material-ui/icons';
import WarningIcon from '@mui/icons-material/Warning';
import PendingIcon from '@mui/icons-material/Pending';
import "./style.css";
import moment from 'moment';
import Utils from '../../utils/utils';
import { Tooltip } from '@mui/material';

function descendingComparator(a, b, orderBy, props) {
  if (props.sortByDate) {
    if (moment(b[orderBy], 'DD/MM/yyyy').isBefore(moment(a[orderBy], 'DD/MM/yyyy'))) {
      return -1
    } else if (moment(b[orderBy], 'DD/MM/yyyy').isAfter(moment(a[orderBy], 'DD/MM/yyyy'))) {
      return 1
    }

    return 0;
  }

  if (typeof b[orderBy] == 'number') {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  }
  else {
    if (b[orderBy].toLowerCase() < a[orderBy].toLowerCase()) {
      return -1;
    }
    if (b[orderBy].toLowerCase() > a[orderBy].toLowerCase()) {
      return 1;
    }
  }
  return 0;
}

function getComparator(order, orderBy, props) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, props)
    : (a, b) => -descendingComparator(a, b, orderBy, props);
}

function stableSort(props, array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  let arr = stabilizedThis.map((el) => el[0]);
  if (!props.total) return arr;

  arr.push(props.total);
  return arr;
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const headCells = props.headCells;
  return (
    <TableHead>
      <TableFilter rows={props.rows} onFilterUpdate={(newData, filtersObject) => {
        onFilter(newData);
      }}>
        <TableCell></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            filterkey={headCell.filter === false ? '' : headCell.id}
            key={headCell.id}
            align={'left'}
            padding={false ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: 'bold', width: headCell.id == '__' ? '20px' : 'auto' }}
          >
            {
              headCell.label !== '' &&
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}

              </TableSortLabel>
            }
          </TableCell>
        ))}
      </TableFilter>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    overflowX: 'scroll',
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

//create your forceUpdate hook
function useForceUpdate() {
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}


export default function EnhancedTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState(props.order || 'asc');
  const [orderBy, setOrderBy] = React.useState(props.headCells[props.sortBy ? props.sortBy : 0].id);
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [isFiltering, setIsFiltering] = useState(false);
  const [filtered, setFiltered] = useState([]);

  var rows = [];
  if (!isFiltering) {
    rows = props.rows;
  } else {
    rows = filtered;
  }


  if (searchText !== '') {
    rows = [];
    props.rows.forEach(r => {
      var found = false;
      var lowerSearch = searchText.toLowerCase();

      props.searchCols.forEach(element => {
        if (r.id && r[element] && r[element].toLowerCase().indexOf(lowerSearch) !== -1)
          found = true;
      });

      if (found === true)
        rows.push(r);
    });

    if (props.fatture) {
      var totale = 0.0;
      rows.forEach(f => {
        totale += parseFloat(f.importo.replace(/\./g, '').replace(/,/g, '.'));
      });
      if (props.onSearch) props.onSearch(String(totale.toFixed(2)));
    }
  } else {
    if (props.fatture) {
      var totale = 0.0;
      rows.forEach(f => {
        totale += parseFloat(f.importo.replace(/\./g, '').replace(/,/g, '.'));
      });
      if (props.onSearch) props.onSearch(String(totale.toFixed(2)));
    }
  }

  const forceUpdate = useForceUpdate();

  const setRows = (r) => {
    setFiltered(r);
    setIsFiltering(r !== props.rows);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.eventName);
      //   setSelected(newSelecteds);
      return;
    }
    //   setSelected([]);
  };


  const handleChange = (e) => {
    setSearchText(e.target.value);
    forceUpdate();

  }

  const resetSearch = () => {
    if (searchRef.current != null) {
      searchRef.current.value = '';
      setSearchText('');
    }
    forceUpdate();
  }

  const handleClick = (id, name, spesa) => {

    if (props.onEventMagazzinoClick) {
      props.onEventMagazzinoClick(
        props.rows.find(r => id ? r.id === id : r.nome == name)
      );
    }

    if (props.onEventClick)
      props.onEventClick(id);

    if (props.onEventFatturaClick)
      props.onEventFatturaClick(spesa, id);

    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    //   setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  var searchRef = React.createRef();

  return (
    <div className={classes.root}>

      <div style={{ marginBottom: 10, display: "inline-flex" }}>
        Cerca:
        <input ref={searchRef} value={searchText} style={{ marginLeft: 10 }} type="text" onChange={(e) => { handleChange(e) }}></input>
        <div style={{ marginLeft: 10, cursor: "pointer" }} onClick={resetSearch}>X</div>
      </div>

      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={props.total ? rows.length + 1 : rows.length}
              headCells={props.headCells}
              rows={rows}
              onFilter={setRows}
            />
            <TableBody>
              {stableSort(props, rows, getComparator(order, orderBy, props))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.eventName);
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={'ROW' + Math.random()}
                      selected={isItemSelected}
                      style={row.avanzamento ? row.avanzamento === 'PAGATA' ? { backgroundColor: '#F0FFD8' } : {} : {}}
                    >
                      <TableCell padding="checkbox" key={'checkbox' + index}>
                      </TableCell>

                      {
                        props.headCells.map(elem => {
                          let bgColor = "";
                          let textColor = "";
                          let invioStato = "";
                          let icon = "";

                          if (elem.chip && row[elem.id]) {
                            let result = Utils.getStatusColors(row[elem.id]);
                            bgColor = result.bgColor;
                            textColor = result.textColor;
                            invioStato = result.invioStato;
                          } else if (elem.icon && row[elem.id]) {
                            switch (row[elem.id]) {
                              case "stato_ok":
                                icon = <CheckCircleIcon size={30} style={{ color: '#7DB154', marginTop: 5, marginBottom: -5 }} />
                                break;
                              case "stato_ko":
                                icon = <CancelIcon size={30} style={{ color: '#FF6262', marginTop: 5, marginBottom: -5 }} />
                                break;
                              case 'Parziale':                     
                                icon = <Tooltip title="Consegnato parzialmente"><LocalShipping size={30} style={{ color: '#E4811E', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'Non Aperto':
                                icon = <Tooltip title="Non aperto"><NotificationImportantIcon size={30} style={{ color: '#7DB154', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'Fuori Fido':
                                icon = <Tooltip title="Fuori fido"><WarningIcon size={30} style={{ color: '#FF6262', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'Non Inviata':
                                icon = <Tooltip title="Email non inviata"><WarningIcon size={30} style={{ color: '#FF6262', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'Alert merce':
                                icon = <Tooltip title="Conferma richiesta"><WarningIcon size={30} style={{ color: '#FF6262', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'info':
                                icon = <InfoRounded size={30} style={{ color: '#cccccc', marginTop: 5, marginBottom: -5, zIndex: 100 }} onClick={() => props.onOpenInfoPanel(row)} />
                                break;
                              case 'Non Confermata':
                                icon = <Tooltip title="Non confermata"><PendingIcon size={30} style={{ color: '#FDD835', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'Confermata':
                                icon = <Tooltip title="Confermata"><CheckCircleIcon size={30} style={{ color: '#7DB154', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              case 'In consegna':
                                icon = <Tooltip title="In consegna"><LocalShipping size={30} style={{ color: '#7DB154', marginTop: 5, marginBottom: -5 }} /></Tooltip>
                                break;
                              default:
                                break;
                            }
                          }

                          return (elem.largeView && row[elem.id] && row[elem.id].includes('!-!-!')) ? (
                            <TableCell key={row[elem.id] + index} onClick={(event) => handleClick(row.id, row.nome || '', row.tipo ? row.tipo === 'Spesa n. fatturata' : false)} align="left">{
                              <>
                                {row[elem.id].split('!-!-!').map(e => (
                                  <p style={{ margin: 'auto', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} key={e.length}>{e}</p>
                                ))}
                              </>
                            }</TableCell>
                          ) : (elem.chip && row[elem.id]) ? (
                            <TableCell key={row[elem.id] + index}>
                              <section style={{ backgroundColor: bgColor, borderRadius: 5, maxWidth: 160 }}>
                                <p style={{ fontSize: 14, fontWeight: 'bold', color: textColor, width: '100%', textAlign: 'center' }}>{invioStato}</p>
                              </section>
                            </TableCell>
                          ) : (elem.icon && row[elem.id]) ? (
                            <TableCell key={row[elem.id] + index} style={{ width: 40 }} align="left">{icon}</TableCell>
                          ) : elem.isLoading ? (
                            <TableCell key={elem.id} onClick={() => handleClick(row.id, row.nome || '', row.tipo ? row.tipo === 'Spesa n. fatturata' : false)} align="left">
                              {row[elem.id] ? (
                                row[elem.id]
                              ) : (
                                <img src={require('../../images/loading2.webp')} style={{ height: 30, marginLeft: 30, alignSelf: 'center', flex: 1 }} />
                              )}
                            </TableCell>
                          ) : (
                            <TableCell key={row[elem.id] + index} onClick={() => handleClick(row.id, row.nome || '', row.tipo ? row.tipo === 'Spesa n. fatturata' : false)} align="left">{row[elem.id]}</TableCell>
                          );
                        })
                      }

                      {(props.onDelete || props.pdf || props.onFattureDelete) && (
                        <TableCell align='right'>
                          <div style={{ display: 'flex' }}>
                            {(props.pdf !== undefined && props.pdf === true) && <PdfIcon style={{ width: '20px' }} onClick={() => {
                              props.onPdfClicked && props.onPdfClicked(row.id);
                            }} />}
                            {props.onExcelPress && <TableChart style={{ width: '20px' }} onClick={() => props.onExcelPress(row.id)} />}
                            {(props.onDelete && row.id) && <DeleteIcon style={{ width: '20px' }} onClick={() => props.onDelete(row.id)} />}
                            {props.onFattureDelete && <DeleteIcon style={{ width: '20px' }} onClick={() => props.onFattureDelete(row.tipo === 'Spesa n. fatturata', row.id)} />}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25]}
          component="div"
          count={rows.length}
          rowsPerPage={25}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

    </div>
  );
}